import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Skeleton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import { motion } from 'framer-motion';
import COLORS from '../../../../../../constants/colors';
import { Link } from 'react-router-dom';
import baseURL from '../../../../user/config';
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import { PrimaryButton, OutlinedButton } from '../../../../../Reusable/Button'; // <--- ADD THIS IMPORT


// Define a cache duration (e.g., 5 minutes in milliseconds)
const CACHE_DURATION = 5 * 60 * 1000; 
const CAMPAIGNS_STORAGE_KEY = 'cached_campaigns';

const CampaignGrid = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const skeletonArray = Array(6).fill(0);

  const formatAmount = (amount) => 'UGX ' + Math.floor(amount).toLocaleString();

// Helper function to truncate text
const truncateText = (text, wordLimit = 20) => {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= wordLimit) return words.join(" ");
  return words.slice(0, wordLimit).join(" ") + "...";
};

  // Sentence case helper
  const toSentenceCase = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      let isCacheStale = true;

      // 1. Try to load from cache
      try {
        const cachedData = localStorage.getItem(CAMPAIGNS_STORAGE_KEY);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          const now = new Date().getTime();

          if (now - timestamp < CACHE_DURATION) {
            // Cache is fresh, use it!
            setCampaigns(data);
            setLoading(false);
            isCacheStale = false;
          }
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
        // Fall through to fetching data if local storage fails
      }

      // 2. Fetch data if cache is stale or non-existent
      if (isCacheStale) {
        setLoading(true); // Ensure loading is true if a fetch is happening
        try {
          const res = await fetch(`${baseURL}/data/backend/campaigns`);
          if (!res.ok) throw new Error('Failed to fetch campaigns');
          const data = await res.json();
          setCampaigns(data);
          
          // 3. Store fresh data in cache
          const cacheItem = {
            data: data,
            timestamp: new Date().getTime(),
          };
          localStorage.setItem(CAMPAIGNS_STORAGE_KEY, JSON.stringify(cacheItem));

        } catch (e) {
          setError(e.message);
          console.error('Error fetching campaigns:', e);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchCampaigns();
  }, []); // Empty dependency array ensures it only runs on mount

  const filtered = campaigns.filter(({ name = '', description = '' }) =>
    (name + description).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Split by category
  const generalCampaigns = filtered.filter((c) => c.category !== 'yamba_cares');
  const yambacaresCampaigns = filtered.filter((c) => c.category === 'yamba_cares');

  const openCampaigns = generalCampaigns.filter((c) => c.status === 'open');
  const closedCampaigns = generalCampaigns.filter((c) => c.status === 'closed');
  const yambaCaresOpen = yambacaresCampaigns.filter((c) => c.status === 'open');
  const yambaCaresCompleted = yambacaresCampaigns.filter((c) => c.status === 'closed');

// The rest of your component (renderCard, renderSkeleton, and return) remains the same.

const renderCard = (campaign, isClosed = false) => {
  const percentage = Math.min((campaign.raised / campaign.goal) * 100, 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{ height: '100%' }}
      key={campaign.id}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          width: '100%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          borderRadius: 4,
          border: 'none',
          backgroundColor: '#fff',
          position: 'relative',
          transition: 'transform 0.2s ease',
          '&:hover': { transform: 'translateY(-8px)' },
        }}
      >

        {/* Image wrapper with overlaid badges */}
        <Box sx={{ position: 'relative', overflow: 'hidden', borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
          <Link
            to={`/campaigns/${campaign.name}?id=${campaign.id}`}
            state={{ campaign }}
            style={{ textDecoration: "none" }}
          >
            <Box
              component="img"
              src={campaign.image_url}
              alt={campaign.name}
              sx={{
                width: '100%',
                height: 260,
                objectFit: 'cover',
                p: 0,
                m: 0,
                display: 'block',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          </Link>

          {/* Category badge — top left */}
          <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
            {campaign.category === 'yamba_cares' ? (
              <Chip label="Yamba Cares" size="small"
                sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(45,74,61,0.88)', color: '#F8B400', border: 'none', backdropFilter: 'blur(4px)' }} />
            ) : campaign.owner_type === 'Organization' ? (
              !!campaign.accepts_recurring ? (
                <Chip label="Org · Recurring" size="small"
                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(45,74,61,0.88)', color: '#F8B400', border: 'none', backdropFilter: 'blur(4px)' }} />
              ) : (
                <Chip label="Organization" size="small"
                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(45,74,61,0.88)', color: '#fff', border: 'none', backdropFilter: 'blur(4px)' }} />
              )
            ) : (
              !!campaign.accepts_recurring ? (
                <Chip label="Individual · Recurring" size="small"
                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(248,180,0,0.92)', color: '#2D4A3D', border: 'none', backdropFilter: 'blur(4px)' }} />
              ) : (
                <Chip label="Individual" size="small"
                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(248,180,0,0.92)', color: '#2D4A3D', border: 'none', backdropFilter: 'blur(4px)' }} />
              )
            )}
          </Box>

          {/* Closed badge — top right */}
          {isClosed && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: 'rgba(211,33,18,0.9)',
                color: '#fff',
                px: 1.5,
                py: 0.4,
                borderRadius: 20,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                backdropFilter: 'blur(4px)',
              }}
            >
              Closed
            </Box>
          )}
        </Box>

        <CardContent
          sx={{
            textAlign: 'left',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
          }}
        >
          <Tooltip title={campaign.name}>
            <Typography
              variant="h6"
              fontWeight="700"
              color={COLORS.primary}
              mb={1}
              noWrap
              sx={{ textTransform: 'none' }}
            >
              {toSentenceCase(campaign.name)}
            </Typography>
          </Tooltip>

                             <Box
  sx={{
    color: COLORS.textSecondary,
    "& p:last-child": { mb: 0 },
    "& ul, & ol": { pl: 2 },
    textAlign: "left",
    lineHeight: 1.5, // <-- reduce line spacing
  }}
>
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeRaw]}
  >
    {truncateText(campaign.description, 20)}
  </ReactMarkdown>
</Box>
          <Typography variant="body2" fontWeight="700" mb={1}>
            {formatAmount(campaign.raised)}{' '}
            <Box component="span" sx={{ color: COLORS.primary, fontWeight: '400' }}>
              raised
            </Box>{' '}
            of {formatAmount(campaign.goal)} goal
          </Typography>

          <Box mt={1}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 5,
                backgroundColor: '#eee',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isClosed ? 'gray' : COLORS.primary,
                },
              }}
            />
          </Box>

          {/* Added percentage donated text below progress bar */}
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              color: COLORS.text,
              fontWeight: 600,
              textAlign: 'right',
            }}
          >
            {percentage.toFixed(0)}% funded
          </Typography>

          <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
            {!isClosed && (
              <PrimaryButton
                to={`/campaigns/${campaign.name}?id=${campaign.id}`}
                component={Link}
                state={{ campaign }}
              
              >
                Donate
              </PrimaryButton>
            )}

            <Typography
              variant="body2"
              fontWeight="700"
              sx={{
                color: isClosed ? 'gray' : COLORS.text,
                fontSize: '0.8rem',
              }}
            >
              {campaign.donations} Donations
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

  const renderSkeleton = () => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${COLORS.border}`,
        boxShadow: 'none',
      }}
    >
      <Skeleton variant="rectangular" height={250} />
      <CardContent>
        <Skeleton variant="text" height={30} width="60%" />
        <Skeleton variant="text" height={20} width="90%" />
        <Skeleton variant="text" height={20} width="40%" />
        <Box mt={2}>
          <Skeleton variant="rectangular" height={10} />
        </Box>
        <Box mt={2} display="flex" justifyContent="space-between">
          <Skeleton variant="rectangular" height={30} width="40%" />
          <Skeleton variant="text" height={20} width="30%" />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" color={COLORS.primary} mb={6}>
        Discover Fundraising Campaigns
      </Typography>

      <TextField
        variant="outlined"
        placeholder="Search campaigns..."
        fullWidth
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          maxWidth: 500,
          mb: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 10, height: 50, pr: 1.5 },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <FaSearch style={{ color: COLORS.accent, fontSize: '1.5rem' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newVal) => setActiveTab(newVal)}
        centered
        sx={{
          mb: 4,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            color: COLORS.text,
          },
          '& .Mui-selected': { color: COLORS.primary },
          '& .MuiTabs-indicator': { backgroundColor: COLORS.primary },
        }}
      >
        <Tab label="Active Campaigns" />
        <Tab label="Yamba Cares Fund" />
      </Tabs>

      {error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : loading ? (
        <Grid container spacing={3} justifyContent="center">
          {skeletonArray.map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              {renderSkeleton()}
            </Grid>
          ))}
        </Grid>
      ) : activeTab === 0 ? (
        /* ─── Active Campaigns tab ─── */
        openCampaigns.length === 0 && closedCampaigns.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <FaSearch style={{ fontSize: '4rem', color: COLORS.accent, opacity: 0.5 }} />
            <Typography variant="h6" color={COLORS.text} mt={2}>
              No fundraising campaigns found.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stay tuned! Fundraising campaigns will appear here soon.
            </Typography>
          </Box>
        ) : (
        <>
          {openCampaigns.length > 0 && (
            <Box mb={6}>
              <Typography variant="h5" fontWeight="bold" mb={2} color={COLORS.primary}>
                Active Campaigns
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                mb={4}
                sx={{
                  mx: 'auto',
                  width: { xs: '100%', md: '60%' },
                }}
              >
                These campaigns still need a final push to reach their funding goals.
                Explore, share, and contribute—every donation moves them closer to
                life‑changing impact. Your support can turn ambitions into achievements
                for families, students, patients, and communities counting on a helping
                hand.
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                {openCampaigns.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    {renderCard(c)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {closedCampaigns.length > 0 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={2}>
                Closed Campaigns
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                mb={4}
                sx={{
                  mx: 'auto',
                  mb: 4,
                  width: { xs: '100%', md: '60%' },
                }}
              >
                Thank you to every donor whose generosity pushed these campaigns over the
                finish line. Your kindness helped real people meet urgent needs—turning
                medical bills into paid receipts, school fees into open doors, and hopes
                into reality. Because of you, these goals became milestones worth
                celebrating.
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                {closedCampaigns.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    {renderCard(c, true)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
        )
      ) : (
        /* ─── Yamba Cares Fund tab ─── */
        yambaCaresOpen.length === 0 && yambaCaresCompleted.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <FaSearch style={{ fontSize: '4rem', color: COLORS.accent, opacity: 0.5 }} />
            <Typography variant="h6" color={COLORS.text} mt={2}>
              No Yamba Cares campaigns yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check back soon for campaigns managed by the Yamba Africa team.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={4} sx={{ mx: 'auto', width: { xs: '100%', md: '60%' } }}>
              <Typography variant="h5" fontWeight="bold" mb={2} color={COLORS.primary}>
                Yamba Cares Fund
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The Yamba Cares Fund supports carefully selected causes managed by the
                Yamba Africa team. Your donation goes directly to verified initiatives
                that create lasting impact in communities across Africa. Together, we
                match generosity with the greatest needs.
              </Typography>
            </Box>

            {yambaCaresOpen.length > 0 && (
              <Box mb={6}>
                <Grid container spacing={3} justifyContent="center">
                  {yambaCaresOpen.map((c) => (
                    <Grid item xs={12} sm={6} md={4} key={c.id}>
                      {renderCard(c)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {yambaCaresCompleted.length > 0 && (
              <Box>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Completed Yamba Cares Campaigns
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                  {yambaCaresCompleted.map((c) => (
                    <Grid item xs={12} sm={6} md={4} key={c.id}>
                      {renderCard(c, true)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );
};

export default CampaignGrid;